import { useEffect, useState } from "react";

import { useRootStore } from "store";
import { useGlobalContext } from "app/store/GlobalContext";
import { Button } from "components/button/Button";
import Dropdown from "components/dropdown/Dropdown";
import { FileInput } from "components/fileInput/FileInput";
import { UPLOAD_FILES_LIMIT } from "components/fileInput/fileInputConsts";
import UploadConfirmationModal from "components/fileInput/UploadConfirmationModal";
import { getIconNameByFileExtension, Icon } from "components/icon/Icon";
import { Input } from "components/input/Input";
import { Loader } from "components/loader/Loader";
import Modal from "components/modal/Modal";
import UnsavedChangesModal from "components/shared/unsavedChangesModal/UnsavedChangesModal";
import useSetErrorOnUpload from "hooks/use-set-error-on-upload";
import FileUploadErrorModal from "components/shared/fileUploadErrorModal/FileUploadErrorModal";

import "./DocumentsDeliveryUploads.scss";

/**
 * Represents a file that is uploaded to the document archive.
 */
export type DeliveryUploadFile = {
  /**
   * Upload file options?
   */
  meta: {
    name: string;
    comment: string;
    category: string;
  };

  /**
   * Binary file data
   */
  file: File;
};

export function DocumentDeliveryUpload() {
  const { global, delivery } = useRootStore((state) => state);
  const { setFilesUploading, sendFile, filesUploading } = delivery;
  const [files, setFiles] = useState<DeliveryUploadFile[]>([]);
  const { editingState, setEditingState, redirectingState } =
    useGlobalContext();

  const { failed, setFailed, failMessage, setFailedWrapper } =
    useSetErrorOnUpload();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState<File[]>();
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());

  // Cleanup function executed on component unmount.
  useEffect(() => {
    return () => {
      setEditingState({ isEditing: false, location: null });
    };
  }, [setEditingState]);

  // Converts file size from bytes to KB/MB 
  // using binary system (1024 bytes = 1 KB, 1024 KB = 1 MB)
  const formatFileSize = (fileSize: number): string => {
    if (fileSize < 1024 * 1024) {
      return (fileSize / 1024).toFixed(2) + " kB";
    }
    return (fileSize / (1024 * 1024)).toFixed(2) + " MB";
  };

  const onItemRemove = (file: DeliveryUploadFile) => {
    const filesRemaining = files.filter((f) => f.meta.name !== file.meta.name);
    setFiles(filesRemaining);
  };

  const onFilesAdded = (addedFiles: DeliveryUploadFile[]) => {
    setFiles([...addedFiles, ...files]);
    setEditingState({ isEditing: true, location: "document-upload" });
  };

  const handleUploadClick = async () => {
    setFilesUploading(true);
    await Promise.all(
      files.map((file) =>
        sendFile(file, global.workingCustomerId, setFiles, setFailedWrapper)
      )
    );
    setFilesUploading(false);

    setEditingState({ isEditing: false, location: null });
    setShowConfirmationModal(false);
  };

  const setFileCategory = (file: DeliveryUploadFile, category: string) => {
    const updatedFiles = files.map((f) =>
      f.meta.name === file.meta.name
        ? { ...f, meta: { ...f.meta, category } }
        : f
    );

    setFiles(updatedFiles);
  };

  const setFileComment = (file: DeliveryUploadFile, comment: string) => {
    const updatedFiles = files.map((f) =>
      f.meta.name === file.meta.name
        ? { ...f, meta: { ...f.meta, comment } }
        : f
    );

    setFiles(updatedFiles);
    if (comment.trim()) {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(file.meta.name);
        return newErrors;
      });
    } else {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.add(file.meta.name);
        return newErrors;
      });
    }
  };

  const handleCommentBlur = (file: DeliveryUploadFile) => {
    if (!file.meta.comment.trim()) {
      setValidationErrors(prev => new Set(prev).add(file.meta.name));
    }
  };

  const tableDropdownOptions = [
    {
      value: "Okategoriserad",
      label: "Ingen kategori"
    },
    {
      value: "Kvitto",
      label: "Kvitto"
    },
    {
      value: "Tidrapport",
      label: "Tidrapport"
    },
    {
      value: "Juridik",
      label: "Juridik"
    }
  ];

  const renderFilesTable = (isInModal = false) =>
    filesUploading ? (
      <div className="place-items-center loader-wrapper mt-5 mb-5">
        <Loader className={"mr-3"} /> Laddar upp filer...
      </div>
    ) : (
      <table className="file-upload-list">
        <thead>
          <tr>
            <th>Namn</th>
            {!isInModal && (
              <>
                <th>Storlek</th>
                <th>Kategori</th>
                <th>Kommentar *</th>
                <th></th>
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {files.map((item, index) => {
            const iconName = getIconNameByFileExtension(item.meta.name);

            return (
              <tr key={`${item.file.name}-${item.file.size}-${index}`}>
                <td data-testid={item.file.name}>
                  <Icon name={iconName} className="mr-2" /> {item.meta.name}
                </td>
                {!isInModal && (
                  <>
                    <td>{formatFileSize(item.file.size)}</td>
                    <td className="category-field">
                      <Dropdown
                        onChange={(selected) => {
                          if (selected && !Array.isArray(selected)) {
                            const selectedOption = selected as {
                              value: string;
                              label: string;
                            };
                            setFileCategory(item, selectedOption.value);
                          }
                        }}
                        placeholder="Välj kategori"
                        options={tableDropdownOptions}
                        value={item.meta.category}
                      />
                    </td>
                    <td className="comment-field">
                      <Input
                        onChange={(e) => setFileComment(item, e.target.value)}
                        placeholder="Kommentar"
                        value={item.meta.comment}
                        onBlur={() => handleCommentBlur(item)}
                        isValid={!validationErrors.has(item.meta.name)}
                      />
                    </td>
                    <td>
                      <Icon
                        data-testid="file-input-remove"
                        name="trash-o"
                        onClick={() => onItemRemove(item)}
                      />
                    </td>
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );

  const handleOnDuplicatedFiles = (duplicates: File[]) => {
    setDuplicateFiles(duplicates);
  };

  const getDuplicatedFilesMessage = () => {
    if (!duplicateFiles) {
      return "";
    }
    return duplicateFiles.map((f) => f.name).join("\n");
  };

  return (
    <>
      {failed && (
        <FileUploadErrorModal failMessage={failMessage} setFailed={setFailed} />
      )}
      {duplicateFiles && duplicateFiles.length > 0 && (
        <Modal
          onDismiss={() => setDuplicateFiles(undefined)}
          modalTitle="duplicate files"
          header={<h1 className="m-0">Dubblettfiler</h1>}
          size="sm"
        >
          <div className="modal-content">
            <p className="modal-subtitle pt-4">
              Följande filer är redan uppladdade:
            </p>
            <p className="pt-2">{getDuplicatedFilesMessage()}</p>
          </div>
        </Modal>
      )}
      <h1>Ladda upp</h1>
      <hr className={"header-border"} />

      {filesUploading && <Loader />}
      {!filesUploading && (
        <>
          <FileInput
            disabled={filesUploading}
            filesNumberLimit={UPLOAD_FILES_LIMIT}
            existingFiles={files.map((f) => f.file)}
            onFilesAdded={(addedFiles) => {
              onFilesAdded(
                addedFiles.map((file) => ({
                  meta: {
                    comment: "",
                    category: "",
                    name: file.name
                  },
                  file
                }))
              );
            }}
            onDuplicatesDetected={handleOnDuplicatedFiles}
          />

          {files.length > 0 && renderFilesTable()}

          {files.length > UPLOAD_FILES_LIMIT && (
            <div>
              <Icon name="exclamation-circle" size="lg" />
              <span>
                Du kan ladda upp max {UPLOAD_FILES_LIMIT} filer åt gången.
              </span>
            </div>
          )}

          <Button
            className="mt-4"
            disabled={
              filesUploading ||
              files.length > UPLOAD_FILES_LIMIT ||
              files.length === 0 || files.some((file) => !file.meta.comment.trim())
            }
            onClick={() => setShowConfirmationModal(true)}
          >
            Ladda upp valda filer
          </Button>
        </>
      )}
      {showConfirmationModal && (
        <UploadConfirmationModal
          onDismiss={() => setShowConfirmationModal(false)}
          onConfirm={handleUploadClick}
          isLoading={filesUploading}
        >
          {renderFilesTable(true)}
        </UploadConfirmationModal>
      )}
      {editingState.isEditing &&
        editingState.location === "document-upload" &&
        redirectingState.isRedirecting && (
          <UnsavedChangesModal text="Du har inte laddat upp din fil. Är du säker på att du vill lämna sidan?" />
        )}
    </>
  );
}
